'use client';

/**
 * Character pop-up, opened from any character card instead of navigating to
 * /catalog/<slug>.
 *
 * The address bar still moves to /catalog/<slug> (history.pushState), so the
 * link is shareable, a reload lands on the real profile page, and the back
 * button closes the pop-up. The modal UI is loaded on first open to keep it
 * out of the sitewide bundle.
 */
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import dynamic from 'next/dynamic';

import { useQueryClient } from '@tanstack/react-query';

import { getCharacterQuickView } from '~/lib/server/actions/character-quick-view-actions';

export interface QuickViewCharacter {
  id: string;
  slug: string;
  name: string;
  age?: number | null;
  image: string | null;
  description?: string | null;
}

interface QuickViewApi {
  open: (character: QuickViewCharacter) => void;
  /** Start loading a character's details (card hover / touch). */
  prefetch: (slug: string) => void;
}

export const quickViewQueryKey = (slug: string) =>
  ['character-quick-view', slug] as const;

export const QUICK_VIEW_STALE_MS = 10 * 60 * 1000;

const QuickViewContext = createContext<QuickViewApi | null>(null);

/** Null outside the provider: cards then fall back to plain navigation. */
export function useCharacterQuickView() {
  return useContext(QuickViewContext);
}

const LazyModal = dynamic(
  () =>
    import('./character-quick-view-modal').then(
      (m) => m.CharacterQuickViewModal,
    ),
  { ssr: false },
);

export function CharacterQuickViewProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [current, setCurrent] = useState<QuickViewCharacter | null>(null);
  // Whether the pop-up added a history entry that closing must pop, and the
  // address it covered.
  const pushed = useRef(false);
  const returnUrl = useRef('');

  const open = useCallback((character: QuickViewCharacter) => {
    const url = `/catalog/${character.slug}`;
    if (pushed.current) {
      window.history.replaceState(window.history.state, '', url);
    } else {
      returnUrl.current = window.location.href;
      window.history.pushState(window.history.state, '', url);
      pushed.current = true;
    }
    setCurrent(character);
  }, []);

  const close = useCallback(() => {
    if (pushed.current) {
      // popstate below clears the pop-up.
      window.history.back();
    } else {
      setCurrent(null);
    }
  }, []);

  useEffect(() => {
    const onPop = () => {
      if (!pushed.current) return;
      pushed.current = false;
      setCurrent(null);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Leaving through a button (chat, call...) navigates forward. The entry we
  // pushed stays in history, so point it back at the page underneath — back
  // from the chat then lands where the user was, not on a /catalog address
  // showing a different page.
  const leave = useCallback(() => {
    if (pushed.current) {
      window.history.replaceState(window.history.state, '', returnUrl.current);
    }
    pushed.current = false;
    setCurrent(null);
  }, []);

  const queryClient = useQueryClient();
  const prefetch = useCallback(
    (slug: string) => {
      void queryClient.prefetchQuery({
        queryKey: quickViewQueryKey(slug),
        queryFn: () => getCharacterQuickView(slug),
        staleTime: QUICK_VIEW_STALE_MS,
      });
    },
    [queryClient],
  );

  const api = useMemo(() => ({ open, prefetch }), [open, prefetch]);

  return (
    <QuickViewContext.Provider value={api}>
      {children}
      {current && (
        <LazyModal
          character={current}
          onClose={close}
          onLeave={leave}
          onOpenCharacter={open}
        />
      )}
    </QuickViewContext.Provider>
  );
}
